import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Badge, Card, CardBody, Table } from "reactstrap";
import { formatGbp } from "../../../helpers/currency";
import { CLIENT_BOOKING_STATUS_COLOR } from "../../../constants/clientPortal";
import Spinners from "../../../components/Common/Spinner";

function ClientRecentBookings({ bookings, loading, error, t }) {
  return (
    <Card className="client-dashboard-section border-0 mb-4">
      <CardBody className="p-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <h5 className="client-dashboard-section__title mb-0">
            {t("clientDashboardRecentBookings", "Recent bookings")}
          </h5>
          <Link to="/client/bookings" className="fw-semibold">
            {t("clientDashboardViewAllBookings", "View all bookings")}
          </Link>
        </div>

        {loading ? (
          <Spinners setLoading={() => {}} />
        ) : error ? (
          <p className="text-danger mb-0">
            {t(
              "clientDashboardBookingsError",
              "Unable to load recent bookings right now."
            )}
          </p>
        ) : !bookings.length ? (
          <div className="client-dashboard-empty text-center py-4">
            <i className="bx bx-calendar-x mb-2" aria-hidden="true" />
            <p className="mb-2">
              {t(
                "clientDashboardNoBookings",
                "You don’t have any bookings yet."
              )}
            </p>
            <Link to="/create-booking" className="fw-semibold">
              {t("clientMenuRequestStaff", "Request Staff")}
            </Link>
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="table-nowrap align-middle mb-0">
              <thead>
                <tr>
                  <th>{t("clientDashboardColBooking", "Booking")}</th>
                  <th>{t("clientDashboardColStatus", "Status")}</th>
                  <th>{t("clientDashboardColSite", "Site")}</th>
                  <th>{t("clientDashboardColAmount", "Amount")}</th>
                  <th>{t("clientDashboardColCreated", "Created")}</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => {
                  const slug = booking.slug || booking.id || "";
                  const status = booking.status || "";
                  const color =
                    CLIENT_BOOKING_STATUS_COLOR[status] || "info";
                  const site =
                    booking.location?.siteName ||
                    booking.location?.fullAddress ||
                    "—";
                  const amount = Number(booking.finalAmount);
                  const created = booking.createdAt
                    ? dayjs(booking.createdAt).format("DD MMM YYYY")
                    : "—";

                  return (
                    <tr key={slug || created}>
                      <td>
                        {slug ? (
                          <Link
                            to={`/client/bookings/${slug}`}
                            className="fw-semibold text-primary"
                          >
                            {slug}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <Badge className={`bg-${color} text-uppercase`}>
                          {status
                            ? String(status).replace(/_/g, " ")
                            : "unknown"}
                        </Badge>
                      </td>
                      <td>{site}</td>
                      <td>{amount ? formatGbp(amount) : "—"}</td>
                      <td>{created}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

ClientRecentBookings.propTypes = {
  bookings: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.any,
  t: PropTypes.func,
};

ClientRecentBookings.defaultProps = {
  loading: false,
  error: null,
};

export default withTranslation()(ClientRecentBookings);
